import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { FileUpload } from "primereact/fileupload";
import "./index.scss";

const RemittanceExceptions = () => {
  const [selectedException, setSelectedException] = useState(null);
  const [resolutionData, setResolutionData] = useState({
    resolutionType: "",
    resolutionAmount: 0,
    resolutionNotes: ""
  });

  const exceptions = [
    { id: 1, severity: "Critical", exceptionId: "EXC001", type: "Amount Mismatch", remittanceNo: "REM20250926001", amount: 25000, age: 2, assignedTo: "John Smith", status: "New" },
    { id: 2, severity: "High", exceptionId: "EXC002", type: "Missing Document", remittanceNo: "REM20250926002", amount: 15000, age: 5, assignedTo: "Sarah Johnson", status: "In Progress" },
    { id: 3, severity: "Medium", exceptionId: "EXC003", type: "Duplicate Entry", remittanceNo: "REM20250925003", amount: 8500, age: 1, assignedTo: "", status: "New" },
    { id: 4, severity: "Low", exceptionId: "EXC004", type: "Date Discrepancy", remittanceNo: "REM20250925004", amount: 3200, age: 3, assignedTo: "Mike Wilson", status: "Resolved" }
  ];

  const severityBodyTemplate = (rowData) => {
    const getSeverity = (severity) => {
      switch (severity) {
        case 'Critical': return 'danger';
        case 'High': return 'warning';
        case 'Medium': return 'info';
        case 'Low': return 'success';
        default: return null;
      }
    };
    return <Tag value={rowData.severity} severity={getSeverity(rowData.severity)} />;
  };

  const statusBodyTemplate = (rowData) => {
    const getSeverity = (status) => {
      switch (status) {
        case 'Resolved': return 'success';
        case 'In Progress': return 'warning';
        case 'New': return 'danger';
        case 'Escalated': return 'info';
        default: return null;
      }
    };
    return <Tag value={rowData.status} severity={getSeverity(rowData.status)} />;
  };

  const actionsBodyTemplate = (rowData) => {
    return (
      <div className="action-buttons">
        <Button icon="pi pi-play" className="p-button-rounded p-button-text" tooltip="Start" disabled={rowData.status !== 'New'} />
        <Button icon="pi pi-check" className="p-button-rounded p-button-success p-button-text" tooltip="Resolve" disabled={rowData.status !== 'In Progress'} />
        <Button icon="pi pi-arrow-up" className="p-button-rounded p-button-warning p-button-text" tooltip="Escalate" />
      </div>
    );
  };

  return (
    <div className="remittance-exceptions">
      <h2>Remittance Exceptions</h2>

      <div className="summary-bar">
        <div className="summary-item">
          <i className="pi pi-exclamation-triangle" />
          <div>
            <div className="value">15</div>
            <div className="label">Total Exceptions</div>
          </div>
        </div>
        <div className="summary-item red">
          <i className="pi pi-clock" />
          <div>
            <div className="value">8</div>
            <div className="label">Unresolved</div>
          </div>
        </div>
        <div className="summary-item orange">
          <i className="pi pi-spin pi-spinner" />
          <div>
            <div className="value">4</div>
            <div className="label">In Progress</div>
          </div>
        </div>
        <div className="summary-item green">
          <i className="pi pi-check-circle" />
          <div>
            <div className="value">3</div>
            <div className="label">Resolved Today</div>
          </div>
        </div>
        <div className="summary-item highlight">
          <i className="pi pi-dollar" />
          <div>
            <div className="value">$51,700</div>
            <div className="label">Total Value at Risk</div>
          </div>
        </div>
      </div>

      <div className="split-view">
        <Card title="Exception List" className="left-panel">
          <div className="filter-section mb-3">
            <Dropdown placeholder="Exception Type" options={[
              { label: "All", value: "All" },
              { label: "Amount Mismatch", value: "Amount Mismatch" },
              { label: "Missing Document", value: "Missing Document" },
              { label: "Duplicate Entry", value: "Duplicate Entry" },
              { label: "Date Discrepancy", value: "Date Discrepancy" }
            ]} className="mr-2" />
            <Dropdown placeholder="Severity" options={[
              { label: "All", value: "All" },
              { label: "Critical", value: "Critical" },
              { label: "High", value: "High" },
              { label: "Medium", value: "Medium" },
              { label: "Low", value: "Low" }
            ]} className="mr-2" />
            <Dropdown placeholder="Status" options={[
              { label: "All", value: "All" },
              { label: "New", value: "New" },
              { label: "In Progress", value: "In Progress" },
              { label: "Resolved", value: "Resolved" },
              { label: "Escalated", value: "Escalated" }
            ]} />
          </div>

          <DataTable
            value={exceptions}
            selection={selectedException}
            onSelectionChange={(e) => setSelectedException(e.value)}
            selectionMode="single"
            dataKey="id"
            stripedRows
          >
            <Column field="severity" header="" body={severityBodyTemplate} style={{ width: '5%' }} />
            <Column field="exceptionId" header="ID" />
            <Column field="type" header="Type" />
            <Column field="remittanceNo" header="Remittance No" />
            <Column field="amount" header="Amount" body={(data) => `$${data.amount.toLocaleString()}`} />
            <Column field="age" header="Age" body={(data) => `${data.age}d`} />
            <Column field="assignedTo" header="Assigned To" />
            <Column field="status" header="Status" body={statusBodyTemplate} />
            <Column header="" body={actionsBodyTemplate} style={{ width: '10%' }} />
          </DataTable>
        </Card>

        <Card title="Exception Details" className="right-panel">
          {selectedException ? (
            <div className="exception-details">
              <div className="detail-section">
                <h4>Exception Information</h4>
                <div className="detail-item">
                  <label>Exception ID:</label>
                  <span>{selectedException.exceptionId}</span>
                </div>
                <div className="detail-item">
                  <label>Type:</label>
                  <span>{selectedException.type}</span>
                </div>
                <div className="detail-item">
                  <label>Severity:</label>
                  <Tag value={selectedException.severity} severity={selectedException.severity.toLowerCase()} />
                </div>
                <div className="detail-item">
                  <label>Created:</label>
                  <span>2025-09-24 10:30</span>
                </div>
                <div className="detail-item">
                  <label>Due By:</label>
                  <span className="highlight">2025-09-28 10:30</span>
                </div>
              </div>

              <div className="detail-section">
                <h4>Transaction Details</h4>
                <div className="detail-item">
                  <label>Remittance No:</label>
                  <span>{selectedException.remittanceNo}</span>
                </div>
                <div className="detail-item">
                  <label>Expected Amount:</label>
                  <span>${selectedException.amount.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Actual Amount:</label>
                  <span>${(selectedException.amount * 0.95).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Difference:</label>
                  <span className="highlight">${(selectedException.amount * 0.05).toLocaleString()}</span>
                </div>
              </div>

              <div className="detail-section">
                <h4>Resolution</h4>
                <div className="p-fluid">
                  <div className="p-field">
                    <label>Resolution Type *</label>
                    <Dropdown
                      value={resolutionData.resolutionType}
                      options={[
                        { label: "Manual Adjustment", value: "Manual Adjustment" },
                        { label: "Write-off", value: "Write-off" },
                        { label: "Create Credit Note", value: "Create Credit Note" },
                        { label: "Create Debit Note", value: "Create Debit Note" },
                        { label: "Reverse Transaction", value: "Reverse Transaction" }
                      ]}
                      onChange={(e) => setResolutionData({ ...resolutionData, resolutionType: e.value })}
                      placeholder="Select Resolution Type"
                    />
                  </div>
                  <div className="p-field">
                    <label>Resolution Amount *</label>
                    <InputNumber
                      value={resolutionData.resolutionAmount}
                      onValueChange={(e) => setResolutionData({ ...resolutionData, resolutionAmount: e.value })}
                      mode="currency"
                      currency="USD"
                    />
                  </div>
                  <div className="p-field">
                    <label>Resolution Notes *</label>
                    <InputTextarea
                      value={resolutionData.resolutionNotes}
                      onChange={(e) => setResolutionData({ ...resolutionData, resolutionNotes: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div className="p-field">
                    <label>Attachments</label>
                    <FileUpload mode="basic" multiple accept=".pdf,.jpg,.png,.doc" />
                  </div>
                </div>

                <div className="action-buttons mt-3">
                  <Button label="Save Progress" className="p-button-secondary mr-2" />
                  <Button label="Resolve Exception" className="p-button-success mr-2" />
                  <Button label="Escalate" className="p-button-warning" />
                </div>
              </div>
            </div>
          ) : (
            <div className="no-selection">
              <p>Select an exception from the list to view details</p>
            </div>
          )}
        </Card>
      </div>

      <div className="action-bar">
        <Button label="Export Exceptions" icon="pi pi-download" className="p-button-secondary mr-2" />
        <Button label="Bulk Assign" icon="pi pi-users" className="p-button-secondary mr-2" disabled={!selectedException} />
        <Button label="Create Report" icon="pi pi-file" className="p-button-primary" />
      </div>
    </div>
  );
};

export default RemittanceExceptions;